import React, { useState } from 'react'
import { useParams, useOutletContext } from 'react-router-dom'
import { useDispatch } from 'react-redux'
import CircularProgress from '@mui/material/CircularProgress'
import Grid from '@mui/material/Grid'
import Footer from './Footer'
import ImageUpload from '../../../../@Common/Pictures/ImageUpload'

const bulletStyle = {
    fontFamily: 'Roboto Regular, Helvetica, sans-serif',
    fontSize: '14px',
    lineHeight: '16px',
    color: '#2A2D36',
    margin: 0,
    paddingLeft: '10px',
    textIndent: '-10px',
}

function BulletDescription({ children }) {
    return <p style={bulletStyle}>{children}</p>
}
import DialogSubsectionHeadline from '../../../../Header/DialogSubsectionHeadline'
import {
    useGetSellerContentQuery,
    useUpdateSellerContentMutation,
} from '../../../../../redux/slices/sellerContentSlice'
import { showSuccesNotification, showErrorNotification, saveAffiliatePicture } from '../../../../../redux/actions'
import { deleteAffiliatePicture } from '../../../../../redux/modules/affiliates/affiliates'
import { useForm } from 'react-hook-form'
import { previewImageDimensionsValid } from '../../../../../validation'

function SellerMarketingAssetsSection() {
    const dispatch = useDispatch()
    const { canUpdate } = useOutletContext()
    const { id: partnerListingId } = useParams()
    const { data: sellerContent, isLoading, refetch } = useGetSellerContentQuery(partnerListingId)
    const [updateSellerContent] = useUpdateSellerContentMutation()
    const [processing, setProcessing] = useState(false)

    const [promoSquareState, setPromoSquareState] = useState(null)
    const [promoTileState, setPromoTileState] = useState(null)
    const [initialized, setInitialized] = useState(false)
    const [promoSquareError, setPromoSquareError] = useState(null)
    const [promoTileError, setPromoTileError] = useState(null)

    const { control, handleSubmit, reset } = useForm()

    React.useEffect(() => {
        if (sellerContent && !initialized) {
            reset({})
            setPromoSquareState({
                url: sellerContent.promoSquare?.pictureId ? sellerContent.promoSquare.imageUrl : null,
                id: sellerContent.promoSquare?.pictureId || 0,
            })
            setPromoTileState({
                url: sellerContent.promoTile?.pictureId ? sellerContent.promoTile.imageUrl : null,
                id: sellerContent.promoTile?.pictureId || 0,
            })
            setInitialized(true)
        }
    }, [sellerContent, initialized, reset])

    const onSubmit = async (values) => {
        // Validate image dimensions on submit (matching Marketplace pattern)
        const errors = {}
        if (promoSquareState?.url && typeof promoSquareState.url !== 'string') {
            const squareError = previewImageDimensionsValid(500, 500, 'sellerPromoSquareImagePreview')
            if (squareError) errors.promoSquare = squareError
        }
        if (promoTileState?.url && typeof promoTileState.url !== 'string') {
            const tileError = previewImageDimensionsValid(585, 360, 'sellerPromoTileImagePreview')
            if (tileError) errors.promoTile = tileError
        }
        if (Object.keys(errors).length > 0) {
            if (errors.promoSquare) setPromoSquareError(errors.promoSquare)
            if (errors.promoTile) setPromoTileError(errors.promoTile)
            return
        }

        setProcessing(true)
        try {
            const freshContent = await refetch().unwrap()
            const promises = []

            if (promoSquareState?.url && typeof promoSquareState.url !== 'string' && promoSquareState.id === 0) {
                const formData = new window.FormData()
                formData.append('file', promoSquareState.url)
                formData.append('seoFilename', 'sellerPromoSquareImage')
                formData.append('targetSize', 200)
                promises.push(dispatch(saveAffiliatePicture(partnerListingId, 'promoSquare', formData)))
            }
            if (promoTileState?.url && typeof promoTileState.url !== 'string' && promoTileState.id === 0) {
                const formData = new window.FormData()
                formData.append('file', promoTileState.url)
                formData.append('seoFilename', 'sellerPromoTileImage')
                formData.append('targetSize', 200)
                promises.push(dispatch(saveAffiliatePicture(partnerListingId, 'promoTile', formData)))
            }

            let squareResult = { id: promoSquareState?.id || 0, url: promoSquareState?.url }
            let tileResult = { id: promoTileState?.id || 0, url: promoTileState?.url }

            if (promises.length > 0) {
                const responses = await Promise.all(promises)
                responses.forEach((r) => {
                    if (r.pictureType === 'promoSquare') {
                        squareResult = { id: r.id, url: r.url }
                    } else if (r.pictureType === 'promoTile') {
                        tileResult = { id: r.id, url: r.url }
                    }
                })
            }

            if (promoSquareState?.url === null && sellerContent?.promoSquare?.pictureId > 0) {
                await dispatch(deleteAffiliatePicture(sellerContent.promoSquare.pictureId))
                squareResult = { id: 0, url: null }
            }
            if (promoTileState?.url === null && sellerContent?.promoTile?.pictureId > 0) {
                await dispatch(deleteAffiliatePicture(sellerContent.promoTile.pictureId))
                tileResult = { id: 0, url: null }
            }

            const payload = {
                sellerId: sellerContent?.sellerId,
                sellerName: freshContent?.sellerName || '',
                sellerWebsite: freshContent?.sellerWebsite || '',
                // Preserve customize page fields (prefer the fresh read so concurrent customize edits aren't clobbered)
                description: freshContent?.description ?? sellerContent?.description ?? '',
                howItWorks: freshContent?.howItWorks ?? sellerContent?.howItWorks ?? '',
                tagline: freshContent?.tagline ?? sellerContent?.tagline ?? '',
                sellerMessage: freshContent?.sellerMessage ?? sellerContent?.sellerMessage ?? '',
                displaySellerMessage: freshContent?.displaySellerMessage ?? sellerContent?.displaySellerMessage ?? false,
                seoSlugLiteral: freshContent?.seoSlugLiteral ?? sellerContent?.seoSlugLiteral ?? '',
                logo: freshContent?.logo ?? sellerContent?.logo ?? { pictureId: 0, altTag: '' },
                desktopHero: freshContent?.desktopHero ?? sellerContent?.desktopHero ?? { pictureId: 0, altTag: '' },
                mobileHero: freshContent?.mobileHero ?? sellerContent?.mobileHero ?? { pictureId: 0, altTag: '' },
                promoSquare: { pictureId: squareResult.id, altTag: typeof squareResult.url === 'string' ? squareResult.url : '' },
                promoTile: { pictureId: tileResult.id, altTag: typeof tileResult.url === 'string' ? tileResult.url : '' },
                location: freshContent?.location ?? null,
            }

            await updateSellerContent(payload).unwrap()

            // Sync image state to the just-persisted ids/urls so a subsequent same-session save does
            // not treat already-uploaded images as new uploads (BTP-24621). The *Result values hold
            // the persisted picture id and url (string) after upload, or the existing values.
            setPromoSquareState({ url: typeof squareResult.url === 'string' ? squareResult.url : null, id: squareResult.id })
            setPromoTileState({ url: typeof tileResult.url === 'string' ? tileResult.url : null, id: tileResult.id })

            dispatch(showSuccesNotification('Seller marketing assets saved successfully.'))
        } catch (err) {
            dispatch(showErrorNotification('There was an error saving seller marketing assets.'))
        } finally {
            setProcessing(false)
        }
    }

    if (isLoading) {
        return (
            <div style={{ position: 'relative', minHeight: '400px', display: 'flex', justifyContent: 'center', alignItems: 'center' }}>
                <CircularProgress />
            </div>
        )
    }

    return (
        <div style={{ position: 'relative' }}>
            {processing && (
                <div style={{
                    position: 'absolute', top: 0, right: 0, bottom: 0, left: 0,
                    minHeight: '400px', backgroundColor: 'rgba(255,255,255,.8)',
                    overflow: 'hidden', zIndex: 100, display: 'flex', justifyContent: 'center', alignItems: 'center',
                }}>
                    <CircularProgress />
                </div>
            )}
            <div style={{ fontSize: '14px' }}>
                <Grid container columnSpacing={5}>
                    <Grid item xs={5}>
                        <DialogSubsectionHeadline marginTop="0">Promo Square</DialogSubsectionHeadline>
                        <BulletDescription>• Supports JPG, JPEG, PNG</BulletDescription>
                        <BulletDescription>• Max file size 2MB</BulletDescription>
                        <BulletDescription>• Dimensions: 500 x 500px</BulletDescription>
                    </Grid>
                    <Grid item xs={7}>
                        <ImageUpload
                            variant="promoSquare"
                            imageUrl={promoSquareState?.url}
                            imageId={promoSquareState?.id || 0}
                            previewId="sellerPromoSquareImagePreview"
                            maxSizeMB={2}
                            error={promoSquareError}
                            validate={setPromoSquareError}
                            onEdit={(file) => setPromoSquareState({ url: file, id: 0 })}
                            onDelete={() => { setPromoSquareState({ url: null, id: 0 }); setPromoSquareError(null) }}
                        />
                    </Grid>
                </Grid>

                <Grid container columnSpacing={5} style={{ marginTop: '24px' }}>
                    <Grid item xs={5}>
                        <DialogSubsectionHeadline marginTop="0">Promo Tile</DialogSubsectionHeadline>
                        <BulletDescription>• Supports JPG, JPEG, PNG</BulletDescription>
                        <BulletDescription>• Max file size 2MB</BulletDescription>
                        <BulletDescription>• Dimensions: 585 x 360px</BulletDescription>
                    </Grid>
                    <Grid item xs={7}>
                        <ImageUpload
                            variant="promoTile"
                            imageUrl={promoTileState?.url}
                            imageId={promoTileState?.id || 0}
                            previewId="sellerPromoTileImagePreview"
                            maxSizeMB={2}
                            error={promoTileError}
                            validate={setPromoTileError}
                            onEdit={(file) => setPromoTileState({ url: file, id: 0 })}
                            onDelete={() => { setPromoTileState({ url: null, id: 0 }); setPromoTileError(null) }}
                        />
                    </Grid>
                </Grid>

            </div>
            {canUpdate && <Footer label="Save" processing={processing} onSubmit={handleSubmit(onSubmit)} />}
        </div>
    )
}

export default SellerMarketingAssetsSection
